import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tooltip, Box, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import { colors } from './Theme/WebTheme';
import CopyButton from './modern/CopyButton';
import { selectingTextIn, pressPoint } from '../common/textSelection';

const useStyles = makeStyles((theme) => ({
  clickableLink: {
    cursor: 'pointer',
    color: colors.HEADER,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 4px',
    borderRadius: '4px',
    // The reference can be selected and copied by hand.
    userSelect: 'text',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(33, 150, 243, 0.08)',
      color: colors.BLUE || '#1976d2',
      textDecoration: 'underline',
    },
    '&:active': {
      backgroundColor: 'rgba(33, 150, 243, 0.12)',
    }
  },
  icon: {
    fontSize: '16px',
    opacity: 0.7,
  },
  loadingContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  disabledLink: {
    cursor: 'not-allowed',
    opacity: 0.6,
    color: 'inherit',
  }
}));

/**
 * ClickableBookingLink Component
 * 
 * Makes booking references and IDs clickable to navigate to booking details.
 * Can search by either booking ID or reference number.
 * 
 * @param {Object} props
 * @param {string} props.value - The value to display (booking ID or reference)
 * @param {string} props.searchBy - Search method: 'id' or 'reference'
 * @param {string} props.bookingId - Direct booking ID if available
 * @param {boolean} props.showIcon - Whether to show an icon next to the link
 * @param {string} props.iconType - Type of icon: 'receipt', 'taxi'
 * @param {boolean} props.disabled - Whether the link is disabled
 * @param {string} props.tooltipText - Custom tooltip text
 * @param {Object} props.style - Custom styles
 * @param {Object} props.state - State to pass to navigation (e.g., pageNo)
 * @param {boolean} props.copy - A copy button after the value (default on:
 *   this link shows a booking reference or ID, which staff copy). A caller
 *   that shows something else with it (a customer's name) passes false.
 *
 * Selecting the value by dragging across it never opens the booking.
 */
const ClickableBookingLink = ({
  value,
  searchBy = 'reference',
  bookingId = null,
  showIcon = true,
  iconType = 'receipt',
  disabled = false,
  tooltipText = null,
  style = {},
  state = {},
  copy = true,
  ...otherProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const auth = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [foundBookingId, setFoundBookingId] = useState(null);
  const pressRef = useRef(null);

  // Get user role for permission checking
  const userRole = auth?.profile?.usertype;

  // Check if user has permission to view booking details
  const hasPermission = userRole && [
    'customer',
    'admin',
    'driver',
    'fleetadmin',
    'finance',
    'callcentersupervisor',
    'callcenteragent'
  ].includes(userRole);

  useEffect(() => {
    if (!value || !bookinglistdata?.bookings || bookingId) return;

    // Find booking based on search method
    const findBooking = () => {
      let foundBooking = null;
      
      if (searchBy === 'reference') {
        // Search by reference number
        foundBooking = bookinglistdata.bookings.find(booking => 
          booking.reference === value
        );
      } else if (searchBy === 'id') {
        // Search by booking ID
        foundBooking = bookinglistdata.bookings.find(booking => 
          booking.id === value || booking.id === value.toString()
        );
      }

      if (foundBooking) {
        setFoundBookingId(foundBooking.id);
      }
    };

    findBooking();
  }, [value, searchBy, bookinglistdata, bookingId]);

  // If bookingId is provided directly, use it
  useEffect(() => {
    if (bookingId) {
      setFoundBookingId(bookingId);
    }
  }, [bookingId]);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || !hasPermission || loading) return;
    // Dragging across the reference to copy it is not a click to open it.
    if (selectingTextIn(e.currentTarget, e, pressRef.current)) return;

    setLoading(true);

    try {
      let targetBookingId = bookingId || foundBookingId;

      // If we still don't have an ID but have a reference, try to find it
      if (!targetBookingId && searchBy === 'reference' && value) {
        const booking = bookinglistdata.bookings?.find(b => b.reference === value);
        if (booking) {
          targetBookingId = booking.id;
        }
      }

      // If we have a booking ID, navigate to the details page
      if (targetBookingId) {
        // Pass the booking ID as the URL parameter
        // Also pass any state (like pageNo) for navigation back
        navigate(`/bookings/bookingdetails/${targetBookingId}`, { state });
      } else {
        // If we can't find the booking, navigate to bookings page with search
        navigate('/bookings', { state: { searchQuery: value, ...state } });
      }
    } catch (error) {
      console.error('Error navigating to booking details:', error);
    } finally {
      setLoading(false);
    }
  };

  // The value with a copy button after it (outside the link and its tooltip,
  // so copying never opens the booking).
  const withCopy = (node) => {
    if (!copy || !value) return node;
    const what = searchBy === 'id'
      ? t('booking_id', { defaultValue: 'Booking ID' })
      : t('booking_page_reference', { defaultValue: 'Booking reference' });
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', maxWidth: '100%', verticalAlign: 'middle' }}>
        {node}
        <CopyButton value={value} what={what} sx={{ my: -0.5 }} />
      </Box>
    );
  };

  // Don't render as link if no permission or no value
  if (!value || !hasPermission) {
    return withCopy(<span style={style} {...otherProps}>{value || '-'}</span>);
  }

  // Determine icon to show
  const getIcon = () => {
    if (!showIcon) return null;
    
    switch (iconType) {
      case 'taxi':
        return <LocalTaxiIcon className={classes.icon} />;
      case 'receipt':
      default:
        return <ReceiptIcon className={classes.icon} />;
    }
  };

  // Determine tooltip text
  const getTooltipText = () => {
    if (tooltipText) return tooltipText;
    
    if (foundBookingId || bookingId) {
      return `View Booking Details: ${value}`;
    }
    
    return `Click to view booking details`;
  };

  const linkContent = (
    <Box
      className={`${classes.clickableLink} ${disabled ? classes.disabledLink : ''}`}
      onMouseDown={(e) => { pressRef.current = pressPoint(e); }}
      onClick={handleClick}
      style={style}
      {...otherProps}
    >
      {getIcon()}
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={14} />
          <span>{value}</span>
        </Box>
      ) : (
        <span>{value}</span>
      )}
    </Box>
  );

  return withCopy(
    <Tooltip title={getTooltipText()} arrow placement="top">
      {linkContent}
    </Tooltip>
  );
};

export default ClickableBookingLink;
